// ---------------------------------------------------------------------------
// Flow Slice — nodes, connections, flows, selection, CRUD operations.
//
// Flutter 1:1: actionflow_canvas_controller.dart (654 LOC) → Zustand slice
// ---------------------------------------------------------------------------

import type {
  ActionFlowNode,
  ActionFlowConnection,
  ActionNodeType,
  ConnectionType,
  FlowMeta,
} from '@/features/flow-editor/models/flow-models'
import type { FlowStep, BasicStep, SwitchStep, StageStep, FlowTrigger } from '@cogo/types-v2'

/**
 * Editor-side address of a nested step array inside a structural step.
 * `'steps'` is the loop (and parallel) body, `'default'` the switch fallback,
 * and `case:<key>` a named switch case. Pure editor addressing — the persisted
 * flow.json schema is unchanged.
 */
export type StepBranch = 'then' | 'else' | 'try' | 'catch' | 'finally' | 'steps' | 'default' | 'onEnter' | 'onSuccess' | 'onError' | 'onFinally' | 'compensate' | 'stages' | `case:${string}`

/**
 * Step address at ARBITRARY nesting depth: a top-level index followed by any
 * number of (branch, index) hops — `[2]`, `[2, 'then', 0]`,
 * `[2, 'then', 0, 'steps', 1]`, … Always odd-length, strictly alternating
 * index/branch; TS cannot express the alternation recursively (circular
 * tuple aliases are rejected), so the tail is loosely typed and the path
 * walkers (`resolveStepAtPath` / `resolveNestedStepArray`) enforce the shape
 * at runtime. The two shallow literal forms predate deep nesting and remain
 * assignable, so historical call sites stay valid.
 */
export type NestedStepPath = [number, StepBranch, number, ...(StepBranch | number)[]]
export type StepPath = [number] | NestedStepPath

/**
 * Address of a nested step ARRAY (a branch) for insert affordances: the
 * owning step's path followed by the branch name — `[2, 'then']`,
 * `[2, 'then', 0, 'steps']`, … Always even-length. Appending an index turns
 * it into the inserted step's NestedStepPath.
 */
export type StepBranchPath = [number, StepBranch, ...(StepBranch | number)[]]

/** Branch vocabulary per structural step type. Optional branches may be
 * created on insert (an empty `else`/`default` is omitted from persisted
 * flows, but the editor must still be able to add the first step into it). */
const STEP_BRANCH_SPEC: Record<string, { branches: string[]; optional: string[]; cases?: boolean }> = {
  conditional: { branches: ['then', 'else'], optional: ['else'] },
  tryCatch: { branches: ['try', 'catch', 'finally'], optional: ['catch', 'finally'] },
  loop: { branches: ['steps'], optional: [] },
  parallel: { branches: ['steps'], optional: [] },
  switch: { branches: ['default'], optional: ['default'], cases: true },
  stage: {
    branches: ['onEnter', 'steps', 'onSuccess', 'onError', 'onFinally', 'compensate'],
    optional: ['onEnter', 'onSuccess', 'onError', 'onFinally', 'compensate'],
  },
  saga: { branches: ['stages'], optional: [] },
}

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key)

/** UI-slice selection fields the structural mutators below remap. The slices
 * share one store; FlowSlice's Set type just doesn't surface them. */
interface StepSelectionState {
  selectedStepIndex: number | null
  selectedStepPath: StepPath | null
}

/**
 * Remap the step selection after a structural mutation (insert/remove/
 * reorder) so the inspector keeps pointing at the SAME step — or clears when
 * that step was removed. Before the QA-3 fix, the mirror-triggered re-seed
 * cleared the selection after every mutation, which incidentally prevented
 * index drift; the identical-content acknowledgment now keeps the selection
 * alive, so mutators must move it explicitly (otherwise a drag-reorder left
 * the inspector showing whichever step LANDED at the old index).
 * `mapIndex` returns the step's new top-level index, or null to clear.
 */
function remapTopLevelSelection(state: unknown, mapIndex: (index: number) => number | null) {
  const ui = state as StepSelectionState
  const path = ui.selectedStepPath
  if (path != null) {
    const mapped = mapIndex(path[0])
    if (mapped == null) {
      ui.selectedStepPath = null
      ui.selectedStepIndex = null
    } else if (mapped !== path[0]) {
      ui.selectedStepPath = [mapped, ...path.slice(1)] as StepPath
      // Keep the legacy index mirror in sync (= path[0], see setSelectedStepPath).
      ui.selectedStepIndex = mapped
    }
    return
  }
  if (ui.selectedStepIndex != null) {
    ui.selectedStepIndex = mapIndex(ui.selectedStepIndex)
  }
}

/**
 * Resolve the nested step array a branch addresses on a structural step.
 * Returns null when the parent type does not support the branch, or when the
 * branch does not exist and `create` is not requested. With `create: true`
 * (mutation paths only — call inside an immer producer) missing optional
 * branches and switch cases are materialized as empty arrays.
 */
export function getNestedSteps(
  parent: FlowStep,
  branch: StepBranch,
  opts?: { create?: boolean },
): FlowStep[] | null {
  const spec = STEP_BRANCH_SPEC[parent.type]
  if (!spec) return null
  if (branch.startsWith('case:')) {
    if (!spec.cases) return null
    const sw = parent as SwitchStep
    const key = branch.slice('case:'.length)
    if (sw.cases && hasOwn(sw.cases, key)) return sw.cases[key]
    if (opts?.create) {
      if (!sw.cases) sw.cases = {}
      sw.cases[key] = []
      return sw.cases[key]
    }
    return null
  }
  if (!spec.branches.includes(branch)) return null
  const record = parent as unknown as Record<string, FlowStep[] | undefined>
  const existing = record[branch]
  if (Array.isArray(existing)) return existing
  if (opts?.create && spec.optional.includes(branch)) {
    record[branch] = []
    return record[branch] as FlowStep[]
  }
  return null
}

/**
 * Total number of steps nested anywhere inside a structural step — every
 * branch (then/else/try/catch/finally/steps/default) plus every switch case,
 * recursively. 0 for basic steps and for structural steps with empty
 * branches. Drives the delete-feedback toast (F4): a structural delete
 * destroys this many descendant steps in one click, so the toast announces
 * the blast radius together with the Cmd+Z undo hint instead of a blocking
 * confirm.
 */
export function countDescendantSteps(step: FlowStep): number {
  const spec = STEP_BRANCH_SPEC[step.type as string]
  if (!spec) return 0
  let count = 0
  const walk = (children: FlowStep[] | null) => {
    if (!children) return
    for (const child of children) count += 1 + countDescendantSteps(child)
  }
  for (const branch of spec.branches) walk(getNestedSteps(step, branch as StepBranch))
  if (spec.cases) {
    const sw = step as SwitchStep
    for (const key of Object.keys(sw.cases ?? {})) walk(getNestedSteps(step, `case:${key}`))
  }
  return count
}

/** Resolve the step a StepPath addresses (any depth), or null when stale. */
export function resolveStepAtPath(steps: FlowStep[], path: StepPath): FlowStep | null {
  let current: FlowStep | undefined = steps[path[0]]
  for (let i = 1; current && i + 1 < path.length; i += 2) {
    const nested = getNestedSteps(current, path[i] as StepBranch)
    current = nested?.[path[i + 1] as number]
  }
  return current ?? null
}

/**
 * Resolve the step ARRAY a nested path's final index lives in — the array a
 * mutation must splice. Walks every intermediate (branch, index) hop strictly
 * (a stale hop returns null); only the FINAL branch honors `create`, so an
 * insert can materialize a missing optional branch (`else` / `default` / new
 * case) without ever fabricating intermediate structure.
 */
export function resolveNestedStepArray(
  steps: FlowStep[],
  path: NestedStepPath,
  opts?: { create?: boolean },
): FlowStep[] | null {
  let parent: FlowStep | undefined = steps[path[0]]
  for (let i = 1; parent && i + 2 < path.length; i += 2) {
    const nested = getNestedSteps(parent, path[i] as StepBranch)
    parent = nested?.[path[i + 1] as number]
  }
  if (!parent) return null
  return getNestedSteps(parent, path[path.length - 2] as StepBranch, opts)
}

/**
 * Remove an empty optional branch after its last child is deleted. An omitted
 * catch rethrows after finally, while `catch: []` suppresses the error, so the
 * distinction is observable runtime behavior rather than JSON cleanup.
 */
function pruneEmptyOptionalBranch(steps: FlowStep[], path: NestedStepPath): void {
  const branch = path[path.length - 2] as StepBranch
  if (branch.startsWith('case:')) return

  const parentPath = path.slice(0, -2) as StepPath
  const parent = resolveStepAtPath(steps, parentPath)
  const spec = parent ? STEP_BRANCH_SPEC[parent.type] : undefined
  if (!parent || !spec?.optional.includes(branch)) return

  const record = parent as unknown as Record<string, FlowStep[] | undefined>
  if (!Array.isArray(record[branch]) || record[branch]?.length !== 0) return

  if (parent.type === 'tryCatch') {
    if (branch === 'catch') {
      // Preserve the schema's catch-or-finally invariant without introducing
      // an empty catch that would swallow the original error.
      if (!Array.isArray(record.finally)) record.finally = []
      delete record.catch
      return
    }
    if (branch === 'finally' && !Array.isArray(record.catch)) return
  }

  delete record[branch]
}

/**
 * Remap the selection after a NESTED insert/remove at `path` (whose final
 * index is the mutation point). A selection is affected iff it addresses the
 * SAME containing array — identical prefix up to the final index — whether it
 * points at a sibling there or descends THROUGH one: the sibling slot shifts
 * (insert/remove before it) or the whole selection dies (its ancestor at the
 * mutation index was removed). This generalizes the old two-level remap; the
 * pre-#970 wrong-step bug was exactly a mutation/selection depth mismatch.
 */
function remapNestedSelection(state: unknown, path: NestedStepPath, kind: 'insert' | 'remove') {
  const ui = state as StepSelectionState
  const sel = ui.selectedStepPath
  if (sel == null || sel.length < path.length) return
  const slot = path.length - 1
  for (let i = 0; i < slot; i++) {
    if (sel[i] !== path[i]) return
  }
  const mutIdx = path[slot] as number
  const selIdx = sel[slot] as number
  if (kind === 'insert') {
    if (selIdx >= mutIdx) {
      const next = [...sel] as unknown as NestedStepPath
      next[slot] = selIdx + 1
      ui.selectedStepPath = next
    }
    return
  }
  if (selIdx === mutIdx) {
    // The selected step — or an ancestor it lives inside — was removed.
    ui.selectedStepPath = null
    ui.selectedStepIndex = null
  } else if (selIdx > mutIdx) {
    const next = [...sel] as unknown as NestedStepPath
    next[slot] = selIdx - 1
    ui.selectedStepPath = next
  }
}

/** Scalar fields updateStepFields may patch, per step type — mirrors how
 * STEP_BRANCH_SPEC scopes branches. Keys outside the step's allowlist are
 * skipped: structural fields (branches/params/type) have dedicated mutators
 * and a scalar write would sever them, while a foreign key (e.g. `condition`
 * on a loop) would be persisted into the owner block's flow.json by the
 * auto-save bridge. Types without scalar fields (tryCatch/parallel) are
 * intentionally absent — every patch on them is a no-op. */
const STEP_FIELD_ALLOWLIST: Record<string, ReadonlySet<string>> = {
  basic: new Set(['name']),
  conditional: new Set(['condition']),
  loop: new Set(['collection', 'itemVar']),
  switch: new Set(['value']),
  stage: new Set(['name', 'failurePolicy']),
  saga: new Set(['compensationFailurePolicy']),
  navigate: new Set(['target', 'sourceExpression']),
  setState: new Set(['target', 'value', 'sourceExpression']),
  callback: new Set(['sourceExpression']),
  event: new Set(['sourceExpression']),
  'domain-operation': new Set(['target', 'sourceExpression', 'tool', 'operation', 'intent', 'query']),
}
import {
  createNode,
  createConnection as makeConnection,
  getOutputPortTypes,
  NODE_WIDTH,
} from '@/features/flow-editor/models/flow-models'
import { LAYOUT } from '@/features/flow-editor/lib/design-tokens'
import { current } from 'immer'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { serializeDiagram } from '@/features/flow-editor/services/flow-diagram'
import type { FlowDiagramData } from '@/features/flow-editor/services/flow-diagram'
// Dynamic, not static (2026-08-17, Wave 4 Lane 3 item 2): flow-slice sits in
// a real cycle with studio-store — studio-store.ts -> project-slice.ts ->
// auto-save-bridge.ts -> flow-editor-store.ts -> flow-slice.ts ->
// studio-store.ts. A PRIOR version of this comment claimed a static import
// here was safe because every read happens "call-time only, inside an action
// body" — that reasoning is wrong: ES module graphs are wired up at IMPORT
// time, not call time, regardless of when the binding is first read. In the
// running app `studio-store.ts` is always the first module evaluated, so the
// cycle resolves in the safe direction. But when anything else imports this
// module's ancestors first (a unit test importing `project-slice.ts`
// directly, reproduced by
// `src/store/__tests__/project-slice-circular-import.test.ts`), the cycle
// reverses: `studio-store.ts` gets pulled in and evaluated while
// `project-slice.ts` is still mid-initialization, and `createProjectSlice` is
// not yet defined when `studio-store.ts`'s own top-level `create()` call
// tries to spread it in.
//
// `studioOwnedBlocks()` below needs `useStudioStore.getState()`
// SYNCHRONOUSLY (it is read mid-action, not deferred), so it cannot await an
// `import()` at the call site the way `selection-slice.ts` does for its
// `queueMicrotask`-deferred reads. Instead: the FIRST call kicks off the
// dynamic import (never at module scope — even non-blocking, an unconditional
// module-scope `import()` here re-enters the still-evaluating `project-slice`
// module graph and reproduces the same race) and caches the resolved binding
// for every call after; until it resolves, `studioOwnedBlocks()` degrades to
// the pre-existing "no owned blocks" fallback already documented below (the
// same fallback blockless/template sessions hit today). In the real app this
// window never has an observable effect: `studioOwnedBlocks()` is only ever
// reached from a flow action a user (or a test) triggers, which is always
// long after `studio-store.ts` has already loaded via the app's own entry
// point.
let cachedUseStudioStore: typeof import('@/store/studio-store').useStudioStore | undefined
let studioStoreImportStarted = false
function resolveStudioOwnedBlocksStore(): void {
  if (studioStoreImportStarted) return
  studioStoreImportStarted = true
  void import('@/store/studio-store').then((m) => {
    cachedUseStudioStore = m.useStudioStore
  })
}
import {
  isFlowNameTaken,
  isReservedFlowName,
  resolveFlowOwner,
  type OwnedBlock,
} from '@/features/flow-editor/services/flow-ownership'

/** Per-flow canvas data stored when switching flows. */
interface FlowCanvasData {
  nodes: ActionFlowNode[]
  connections: ActionFlowConnection[]
}

export interface FlowSlice {
  // Flow management
  flows: FlowMeta[]
  selectedFlowId: string | null
  flowDataMap: Record<string, FlowCanvasData>
  selectFlow: (id: string) => void
  /**
   * Create a flow — duplicate-guarded (F8, mirrors renameFlow's F3 guard):
   * the persistence key is the NAME, so two flows on one name race two
   * FlowMetas onto a single name-keyed flowKey (the same fork class F3
   * closed for rename). Rejected names (isFlowNameTaken: editor FlowMetas +
   * every block flow-map declaration, full-then-bare, case-insensitive)
   * return null so every creation surface — Behavior CreateFlowForm,
   * FlowStepPanel, the Page-inspector picker, the unified list, the agent
   * addFlow tool — is protected; callers surface the rejection themselves.
   * Returns the new flow id otherwise (and selects the new flow).
   */
  createFlow: (name: string, trigger?: FlowTrigger) => string | null
  /** Store-only UNGUARDED FlowMeta creation — the hydration internal behind
   *  createFlow (story-canvas loads persisted flows whose names ARE declared
   *  in blocks; the duplicate guard must not reject those). Never call from
   *  UI creation paths. */
  applyFlowCreate: (name: string, trigger?: FlowTrigger) => string
  /**
   * Delete a flow — a PERSISTED operation (F3): dispatches
   * `cogo.studio.deleteFlow`, which removes the owner block's declaration,
   * every event binding (block + element level), queues the saves, and is
   * undoable. Falls back to the store-only removal when the studio tools are
   * not registered (component tests, no MCP bridge).
   */
  deleteFlow: (id: string) => void
  /**
   * Rename a flow — a PERSISTED operation (F3): guards against duplicate
   * names (isFlowNameTaken, full-then-bare, case-insensitive — case-only
   * self-renames stay legal), then dispatches `cogo.studio.renameFlow`,
   * which re-keys the owner block's flow JSON, rewrites every binding, and
   * queues the saves. Returns false when the name is taken (the UI keeps the
   * edit state and surfaces the error); true otherwise. FlowMeta.id never
   * changes on rename, so prior undo entries keyed by flowId stay valid.
   */
  renameFlow: (id: string, name: string) => boolean
  /** Store-only FlowMeta rename — the tool-handler internal behind
   *  renameFlow. Never call from UI paths: it persists nothing. */
  applyFlowRename: (id: string, name: string) => void
  /** Store-only flow removal (list/canvas/selection cleanup) — the
   *  tool-handler internal behind deleteFlow. Never call from UI paths. */
  applyFlowDelete: (id: string) => void
  /** Bind a flow to an element event (block-level `events.bindings`).
   *  Optional `elementBinding` (RUX-01/COR-4, Page-surface dual-write): the
   *  tool handler ALSO writes the element-level `element.events` copy so the
   *  bind is ONE undoable bus operation covering both representations.
   *  Flow-editor surfaces bind block-level only — omit it there. */
  bindFlowToEvent: (
    flowId: string,
    blockName: string,
    elementId: string,
    eventType: string,
    elementBinding?: unknown,
  ) => void
  /** Remove a block-level event binding. `options.removeElementEvent`
   *  (RUX-01/COR-4) additionally removes the element-level copy through the
   *  same single undoable bus operation. */
  unbindFlowFromEvent: (
    blockName: string,
    elementId: string,
    eventType: string,
    options?: { removeElementEvent?: boolean },
  ) => void

  // Diagram persistence
  diagramMap: Record<string, FlowDiagramData>
  setDiagramMap: (map: Record<string, FlowDiagramData>) => void
  saveDiagramForFlow: (flowId: string) => void
  getDiagramMap: () => Record<string, FlowDiagramData>

  // Canvas state
  nodes: ActionFlowNode[]
  connections: ActionFlowConnection[]
  nextNodeId: number

  // Node CRUD
  addNode: (type: ActionNodeType, actionType: string, position: { x: number; y: number }, label?: string) => string
  removeNode: (id: string) => void
  moveNode: (id: string, position: { x: number; y: number }) => void
  duplicateNodes: (ids: string[]) => void
  updateNodeParameters: (id: string, params: Record<string, unknown>) => void
  updateNodeLabel: (id: string, label: string) => void

  // Connection CRUD
  createConnection: (source: string, target: string, type: ConnectionType, outputIndex?: number) => void
  deleteConnection: (id: string) => void

  // Selection
  selectedNodeIds: string[]
  selectNode: (id: string, additive?: boolean) => void
  clearSelection: () => void
  selectAll: () => void
  removeSelectedNodes: () => void
  duplicateSelectedNodes: () => void

  // Palette insertion
  addFromPalette: (nodeType: ActionNodeType, actionType: string, label: string, fromPort?: ConnectionType) => string

  // History
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void

  // Local flow steps (populated when a flow is selected, editable)
  flowSteps: FlowStep[]
  /**
   * True only after a user-initiated step mutation (add/remove/update/reorder/
   * nested edit) since the last hydration or flow switch. The auto-save bridge
   * persists flowSteps ONLY while this is set — selecting or hydrating a flow
   * must never queue a save (F50: selection-triggered saves wrote stale or
   * empty step arrays over persisted flows).
   */
  flowStepsDirty: boolean
  /**
   * Id of the flow whose content the editing buffer (flowSteps) currently
   * holds, recorded at seed time (FlowStepPanel's seeding effect). null until
   * the first seed. The StepInspector resolves steps store-first ONLY while
   * this matches selectedFlowId — so a flow that exists only in the editor
   * (not yet persisted into any block) is still inspectable, and the panel
   * and inspector always read the SAME array (QA-2a). After selectFlow /
   * createFlow this intentionally keeps pointing at the PREVIOUS flow until
   * the panel re-seeds: the buffer really does still hold that flow's steps.
   */
  flowStepsFlowId: string | null
  setFlowSteps: (steps: FlowStep[], flowId?: string | null) => void
  /**
   * EXPLICIT F50 recovery (operator-approved migration path): replace the
   * buffer with the intact shadowed copy of a corrupted flow and mark it
   * DIRTY — unlike setFlowSteps' seed semantics, this IS a user edit, so the
   * auto-save bridge persists the recovered steps to the owner block through
   * the normal sealed pipeline. Never called automatically (#969 invariant).
   */
  recoverFlowSteps: (steps: FlowStep[], flowId: string | null) => void
  /**
   * Acknowledge a re-seed whose incoming content is identical to the editing
   * buffer (e.g. the auto-save mirror writing the just-edited steps back into
   * currentProject): re-keys the buffer to `flowId` and clears flowStepsDirty
   * exactly like a real seed would (#969 contract), WITHOUT replacing the
   * array — step selection and referential identity survive (QA-3).
   */
  ackFlowStepsSeed: (flowId: string | null) => void
  addStep: (index: number, step: FlowStep) => void
  removeStep: (index: number) => void
  updateStep: (path: StepPath, params: Record<string, unknown>) => void
  /**
   * Patch scalar TOP-LEVEL fields of a (typed) step: conditional.condition,
   * loop.collection / loop.itemVar, switch.value, basic.name. Keys outside
   * the step type's allowlist (STEP_FIELD_ALLOWLIST) are rejected — both
   * structural fields (then/else/try/catch/steps/cases/default/params/type)
   * and foreign scalars. Marks flowStepsDirty only when a field changed.
   */
  updateStepFields: (path: StepPath, patch: Record<string, unknown>) => void
  /**
   * Edit-time legacy-action migration (QA-4): atomically rewrite ONE basic
   * step's action name to its canonical equivalent together with the
   * canonical param shape. Only ever invoked from a real user commit (form
   * blur with a changed value, or the explicit "Update to current format"
   * affordance) — never on open/tab-through, and never in bulk.
   */
  migrateStepAction: (path: StepPath, canonicalAction: string, params: Record<string, unknown>) => void
  addSwitchCase: (path: StepPath, caseKey: string) => void
  renameSwitchCase: (path: StepPath, oldKey: string, newKey: string) => void
  removeSwitchCase: (path: StepPath, caseKey: string) => void
  reorderSteps: (from: number, to: number) => void
  insertNestedStep: (path: NestedStepPath, step: FlowStep) => void
  removeNestedStep: (path: NestedStepPath) => void

  // Import/Export
  setCanvasState: (nodes: ActionFlowNode[], connections: ActionFlowConnection[]) => void
}

type Set = (fn: (state: FlowSlice) => void) => void
type Get = () => FlowSlice

/** Blocks of the current project, in the shape the ownership helpers read.
 *  Empty for blockless/template sessions — the guards then only see editor
 *  FlowMetas, and owner resolution falls back to the draft path. */
function studioOwnedBlocks(): Record<string, OwnedBlock> {
  if (!cachedUseStudioStore) {
    resolveStudioOwnedBlocksStore()
    return {}
  }
  return (cachedUseStudioStore.getState().currentProject?.blocks ?? {}) as Record<string, OwnedBlock>
}

/** Mirrors dispatchFlowStepsOp's manifest check (F4): component tests render
 *  flow surfaces without registerStudioTools(), so the promoted actions fall
 *  back to the direct store mutation — applied, just not persisted/undoable. */
function isStudioFlowToolRegistered(tool: 'renameFlow' | 'deleteFlow'): boolean {
  return mcpBus.getManifest().some((t) => t.name === `cogo.studio.${tool}`)
}

let warnedFlowToolFallback = false
function warnFlowToolFallbackOnce(tool: string): void {
  if (warnedFlowToolFallback) return
  warnedFlowToolFallback = true
  console.warn(
    `[flow-slice] cogo.studio.${tool} unavailable (tool not registered) — ` +
      'applying the mutation store-only; it will NOT persist and is NOT undoable.',
  )
}

export function createFlowSlice(set: Set, get: Get, _api: unknown): FlowSlice {

  return {
    flows: [],
    selectedFlowId: null,
    flowDataMap: {},
    diagramMap: {},
    nodes: [],
    connections: [],
    nextNodeId: 1,
    selectedNodeIds: [],
    canUndo: false,
    canRedo: false,
    flowSteps: [],
    flowStepsDirty: false,
    flowStepsFlowId: null,

    // ---- Flow management ----

    selectFlow: (id) =>
      set((state) => {
        if (state.selectedFlowId === id) return
        // Save current flow's canvas data + diagram
        if (state.selectedFlowId) {
          state.flowDataMap[state.selectedFlowId] = {
            nodes: current(state.nodes),
            connections: current(state.connections),
          }
          state.diagramMap[state.selectedFlowId] = serializeDiagram(
            current(state.nodes),
            current(state.connections),
          )
        }
        // Restore target flow's canvas data
        const saved = state.flowDataMap[id]
        state.selectedFlowId = id
        state.nodes = saved?.nodes ?? []
        state.connections = saved?.connections ?? []
        state.selectedNodeIds = []
        // flowSteps still holds the PREVIOUS flow's steps until FlowStepPanel
        // re-seeds them — mark clean so the stale array is never persisted
        // under the newly selected flow's name (F50 verifier addendum).
        state.flowStepsDirty = false
      }),

    createFlow: (name, trigger = 'event') => {
      // Reserved-name guard (CS-1): "__proto__"/"constructor"/"prototype"
      // can never be safe flow-map keys — a bracket write with "__proto__"
      // hits the inherited setter (zero own keys → JSON-empty persist).
      if (isReservedFlowName(name)) return null
      // Duplicate guard BEFORE any mutation (F8): a taken name would fork two
      // FlowMetas onto one name-keyed persistence flowKey. Checked against
      // BOTH the editor list and every block flow-map declaration, so the
      // Page surface is protected even when the Behavior surface (the only
      // flowEditorStore hydration) never mounted this session.
      if (isFlowNameTaken(get().flows, studioOwnedBlocks(), name)) return null
      return get().applyFlowCreate(name, trigger)
    },

    applyFlowCreate: (name, trigger = 'event') => {
      const id = `flow_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
      set((state) => {
        // Save current flow's canvas data before switching
        if (state.selectedFlowId) {
          state.flowDataMap[state.selectedFlowId] = {
            nodes: current(state.nodes),
            connections: current(state.connections),
          }
        }
        state.flows.push({ id, name, trigger })
        state.selectedFlowId = id
        state.nodes = []
        state.connections = []
        state.selectedNodeIds = []
        state.flowStepsDirty = false
      })
      return id
    },

    applyFlowDelete: (id) =>
      set((state) => {
        state.flows = state.flows.filter((f) => f.id !== id)
        delete state.flowDataMap[id]
        if (state.selectedFlowId === id) {
          const nextFlowId = state.flows[0]?.id ?? null
          state.selectedFlowId = nextFlowId
          // Restore the next flow's data
          const saved = nextFlowId ? state.flowDataMap[nextFlowId] : undefined
          state.nodes = saved?.nodes ?? []
          state.connections = saved?.connections ?? []
          state.selectedNodeIds = []
          state.flowStepsDirty = false
        }
      }),

    deleteFlow: (id) => {
      const flowMeta = get().flows.find((f) => f.id === id)
      if (!flowMeta) return
      if (!isStudioFlowToolRegistered('deleteFlow')) {
        warnFlowToolFallbackOnce('deleteFlow')
        get().applyFlowDelete(id)
        return
      }
      // Owner blockName mirrors the F4 editFlowSteps param convention: the
      // undo middleware's mergeKey and the validation middleware's existence
      // check both key off params.blockName + params.name.
      const ownerBlockName = resolveFlowOwner(studioOwnedBlocks(), flowMeta.name)?.blockName
      void mcpBus.executeTrustedUi({
        tool: 'cogo.studio.deleteFlow',
        params: {
          flowId: id,
          ...(ownerBlockName !== undefined ? { blockName: ownerBlockName } : {}),
          name: flowMeta.name,
        },
      })
    },

    applyFlowRename: (id, name) =>
      set((state) => {
        const flow = state.flows.find((f) => f.id === id)
        if (flow) flow.name = name
      }),

    renameFlow: (id, name) => {
      const flowMeta = get().flows.find((f) => f.id === id)
      if (!flowMeta) return false
      // Exact self-rename: nothing to do (case-only changes fall through —
      // they are real persisted key rewrites).
      if (flowMeta.name === name) return true
      // Reserved-name guard (CS-1) — same reasoning as createFlow.
      if (isReservedFlowName(name)) return false
      const blocks = studioOwnedBlocks()
      // Duplicate guard BEFORE any dispatch: the persistence key is the
      // name, so two flows on one name silently merge/overwrite (F3 leg 3).
      if (isFlowNameTaken(get().flows, blocks, name, id)) return false
      if (!isStudioFlowToolRegistered('renameFlow')) {
        warnFlowToolFallbackOnce('renameFlow')
        get().applyFlowRename(id, name)
        return true
      }
      const ownerBlockName = resolveFlowOwner(blocks, flowMeta.name)?.blockName
      void mcpBus.executeTrustedUi({
        tool: 'cogo.studio.renameFlow',
        params: {
          flowId: id,
          ...(ownerBlockName !== undefined ? { blockName: ownerBlockName } : {}),
          name: flowMeta.name,
          newName: name,
        },
      })
      return true
    },

    bindFlowToEvent: (flowId, blockName, elementId, eventType, elementBinding) => {
      const flowMeta = get().flows.find((f) => f.id === flowId)
      if (!flowMeta) return

      // Persist the event→flow binding via MCP bus
      const eventKey = `${elementId}:${eventType}`
      mcpBus.executeTrustedUi({
        tool: 'cogo.studio.addEventBinding',
        params: {
          blockName,
          eventKey,
          flow: flowMeta.name,
          // RUX-01/COR-4: optional element leg — the handler writes the
          // element-level copy too, with ONE inverse covering both legs.
          ...(elementBinding !== undefined
            ? { element: { elementId, eventType, binding: elementBinding } }
            : {}),
        },
      })
    },

    unbindFlowFromEvent: (blockName, elementId, eventType, options) => {
      // Removal is a distinct tool — binding an empty flow id is a no-op
      // (bindFlowToEvent returns before dispatching when the id is unknown).
      const eventKey = `${elementId}:${eventType}`
      mcpBus.executeTrustedUi({
        tool: 'cogo.studio.removeEventBinding',
        params: {
          blockName,
          eventKey,
          // RUX-01/COR-4: optional element leg — the handler removes the
          // element-level copy too, restorable by the same inverse.
          ...(options?.removeElementEvent ? { element: { elementId, eventType } } : {}),
        },
      })
    },

    // ---- Node CRUD ----

    addNode: (type, actionType, position, label) => {
      const s = get()
      const nodeId = `af_${s.nextNodeId}`
      set((state) => {
        state.nextNodeId++
        const node = createNode(nodeId, type, actionType, position, label)
        state.nodes.push(node)
      })
      return nodeId
    },

    removeNode: (id) =>
      set((state) => {
        state.nodes = state.nodes.filter((n) => n.id !== id)
        state.connections = state.connections.filter(
          (c) => c.sourceNodeId !== id && c.targetNodeId !== id,
        )
        state.selectedNodeIds = state.selectedNodeIds.filter((sid) => sid !== id)
      }),

    moveNode: (id, position) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === id)
        if (node) {
          node.position = position
        }
      }),

    duplicateNodes: (ids) =>
      set((state) => {
        const newIds: Record<string, string> = {}
        for (const id of ids) {
          const orig = state.nodes.find((n) => n.id === id)
          if (!orig) continue
          const newId = `af_${state.nextNodeId++}`
          newIds[id] = newId
          const plain = current(orig)
          state.nodes.push({
            ...plain,
            id: newId,
            position: { x: plain.position.x + 100, y: plain.position.y + 20 },
            executionStatus: 'idle',
            executionDetail: undefined,
          })
        }
        // Duplicate internal connections
        for (const c of state.connections) {
          if (newIds[c.sourceNodeId] && newIds[c.targetNodeId]) {
            const newConn = makeConnection(
              newIds[c.sourceNodeId],
              newIds[c.targetNodeId],
              c.connectionType,
              c.outputIndex,
            )
            state.connections.push(newConn)
          }
        }
        state.selectedNodeIds = Object.values(newIds)
      }),

    updateNodeParameters: (id, params) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === id)
        if (node) {
          node.parameters = { ...node.parameters, ...params }
        }
      }),

    updateNodeLabel: (id, label) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === id)
        if (node) {
          node.label = label
        }
      }),

    // ---- Connection CRUD (n8n: one per output port) ----

    createConnection: (source, target, type, outputIndex = 0) =>
      set((state) => {
        // Validate: no self-connect
        if (source === target) return

        // Validate: target must have input port (not trigger)
        const targetNode = state.nodes.find((n) => n.id === target)
        if (!targetNode || targetNode.nodeType === 'trigger') return

        // Remove existing connection from same source port (n8n rule)
        state.connections = state.connections.filter(
          (c) =>
            !(c.sourceNodeId === source && c.connectionType === type && c.outputIndex === outputIndex),
        )

        // Remove existing connection to same target input (one input allowed)
        state.connections = state.connections.filter(
          (c) => c.targetNodeId !== target,
        )

        state.connections.push(makeConnection(source, target, type, outputIndex))
      }),

    deleteConnection: (id) =>
      set((state) => {
        state.connections = state.connections.filter((c) => c.id !== id)
      }),

    // ---- Selection ----

    selectNode: (id, additive = false) =>
      set((state) => {
        if (additive) {
          const idx = state.selectedNodeIds.indexOf(id)
          if (idx >= 0) {
            state.selectedNodeIds.splice(idx, 1)
          } else {
            state.selectedNodeIds.push(id)
          }
        } else {
          state.selectedNodeIds = [id]
        }
      }),

    clearSelection: () =>
      set((state) => {
        state.selectedNodeIds = []
      }),

    selectAll: () =>
      set((state) => {
        state.selectedNodeIds = state.nodes.map((n) => n.id)
      }),

    removeSelectedNodes: () => {
      const ids = get().selectedNodeIds
      if (ids.length === 0) return
      set((state) => {
        const idSet = new Set(ids)
        state.nodes = state.nodes.filter((n) => !idSet.has(n.id))
        state.connections = state.connections.filter(
          (c) => !idSet.has(c.sourceNodeId) && !idSet.has(c.targetNodeId),
        )
        state.selectedNodeIds = []
      })
    },

    duplicateSelectedNodes: () => {
      const ids = get().selectedNodeIds
      if (ids.length > 0) get().duplicateNodes(ids)
    },

    // ---- Palette insertion (smart positioning + auto-connect) ----

    addFromPalette: (nodeType, actionType, label, fromPort) => {
      const s = get()
      let position = { x: 200, y: 200 }

      if (s.selectedNodeIds.length > 0) {
        const selectedNodes = s.nodes.filter((n) => s.selectedNodeIds.includes(n.id))
        const rightmost = selectedNodes.reduce((a, b) => (a.position.x > b.position.x ? a : b))
        let yOffset = 0
        // Multi-output node: offset Y based on port index
        if (fromPort) {
          const outputTypes = getOutputPortTypes(rightmost.nodeType)
          if (outputTypes.length >= 2) {
            const portIdx = outputTypes.indexOf(fromPort)
            const mid = (outputTypes.length - 1) / 2
            yOffset = (portIdx - mid) * LAYOUT.portYOffset
          }
        }
        position = { x: rightmost.position.x + NODE_WIDTH + LAYOUT.insertionGap, y: rightmost.position.y + yOffset }
      } else if (s.nodes.length > 0) {
        const rightmost = s.nodes.reduce((a, b) => (a.position.x > b.position.x ? a : b))
        position = { x: rightmost.position.x + NODE_WIDTH + LAYOUT.insertionGap, y: rightmost.position.y }
      }

      const nodeId = `af_${s.nextNodeId}`
      set((state) => {
        state.nextNodeId++
        const node = createNode(nodeId, nodeType, actionType, position, label)
        state.nodes.push(node)

        // Auto-connect to selected/rightmost node
        if (state.selectedNodeIds.length === 1) {
          const sourceId = state.selectedNodeIds[0]
          const sourceNode = state.nodes.find((n) => n.id === sourceId)
          if (sourceNode && nodeType !== 'trigger') {
            if (fromPort) {
              // Connect to the specific port requested
              state.connections.push(makeConnection(sourceId, nodeId, fromPort))
            } else {
              const outputTypes = getOutputPortTypes(sourceNode.nodeType)
              for (const connType of outputTypes) {
                const existing = state.connections.find(
                  (c) => c.sourceNodeId === sourceId && c.connectionType === connType,
                )
                if (!existing) {
                  state.connections.push(makeConnection(sourceId, nodeId, connType))
                  break
                }
              }
            }
          }
        }

        state.selectedNodeIds = [nodeId]
      })
      return nodeId
    },

    // ---- Diagram persistence ----

    setDiagramMap: (map) =>
      set((state) => {
        state.diagramMap = map
      }),

    saveDiagramForFlow: (flowId) =>
      set((state) => {
        state.diagramMap[flowId] = serializeDiagram(
          current(state.nodes),
          current(state.connections),
        )
      }),

    getDiagramMap: () => {
      const state = get()
      // Ensure the active flow's current canvas is included
      const map = { ...state.diagramMap }
      if (state.selectedFlowId) {
        map[state.selectedFlowId] = serializeDiagram(
          state.nodes,
          state.connections,
        )
      }
      return map
    },

    // ---- History (delegated to MCP undo middleware) ----

    undo: () => { mcpBus.executeTrustedUi({ tool: 'cogo.studio.undo', params: {} }) },
    redo: () => { mcpBus.executeTrustedUi({ tool: 'cogo.studio.redo', params: {} }) },

    // ---- Local flow steps ----

    setFlowSteps: (steps, flowId = null) =>
      set((state) => {
        // Hydration/seed path (FlowStepPanel) — replacing the array wholesale
        // is never a user edit, so it must not arm the auto-save bridge.
        state.flowSteps = steps
        state.flowStepsDirty = false
        state.flowStepsFlowId = flowId
      }),

    recoverFlowSteps: (steps, flowId) =>
      set((state) => {
        // Deep-clone: the recovered array comes from currentProject's
        // recoverableFlows record; the editing buffer must never alias
        // persisted-store objects (the bridge mirrors edits back into
        // currentProject and aliasing would self-acknowledge them).
        state.flowSteps = JSON.parse(JSON.stringify(steps)) as FlowStep[]
        state.flowStepsFlowId = flowId
        // The explicit Recover click IS a real user edit — arm the bridge.
        state.flowStepsDirty = true
      }),

    ackFlowStepsSeed: (flowId) =>
      set((state) => {
        // No-op re-seed: the incoming content equals the buffer, so keep the
        // array (selection + form instances survive) but apply the same seed
        // semantics setFlowSteps would — dirty cleared, buffer re-keyed.
        state.flowStepsDirty = false
        state.flowStepsFlowId = flowId
      }),

    addStep: (index, step) =>
      set((state) => {
        state.flowSteps.splice(index, 0, step)
        state.flowStepsDirty = true
        remapTopLevelSelection(state, (i) => (i >= index ? i + 1 : i))
      }),

    removeStep: (index) =>
      set((state) => {
        if (index >= 0 && index < state.flowSteps.length) {
          state.flowSteps.splice(index, 1)
          state.flowStepsDirty = true
          remapTopLevelSelection(state, (i) => (i === index ? null : i > index ? i - 1 : i))
        }
      }),

    updateStep: (path, params) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (step && 'params' in step) {
          (step as BasicStep).params = params
          state.flowStepsDirty = true
        }
      }),

    migrateStepAction: (path, canonicalAction, params) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (!step || !('action' in step) || !('params' in step)) return
        // Structural steps may carry a vestigial legacy `action` field (e.g.
        // the canonical-todo fixture's conditional has action:
        // 'conditional_action') — their branches/fields have dedicated
        // mutators; rewriting them here would corrupt the step. Persisted
        // BASIC steps often omit `type`, so absence counts as basic.
        if (step.type != null && STEP_BRANCH_SPEC[step.type] !== undefined) return
        const basic = step as BasicStep
        basic.action = canonicalAction
        basic.params = params
        state.flowStepsDirty = true
      }),

    updateStepFields: (path, patch) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (!step) return
        const allowed = STEP_FIELD_ALLOWLIST[step.type]
        if (!allowed) return
        const record = step as unknown as Record<string, unknown>
        let changed = false
        for (const [key, value] of Object.entries(patch)) {
          if (!allowed.has(key)) continue
          if (
            step.type === 'stage' &&
            key === 'failurePolicy' &&
            value === 'continue' &&
            !(step as StageStep).onError?.length
          ) continue
          if (Object.is(record[key], value)) continue
          record[key] = value
          changed = true
        }
        if (changed) state.flowStepsDirty = true
      }),

    addSwitchCase: (path, caseKey) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (!step || step.type !== 'switch') return
        const key = caseKey.trim()
        if (!key) return
        const sw = step as SwitchStep
        if (!sw.cases) sw.cases = {}
        if (hasOwn(sw.cases, key)) return // never clear an existing case
        sw.cases[key] = []
        state.flowStepsDirty = true
      }),

    renameSwitchCase: (path, oldKey, newKey) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (!step || step.type !== 'switch') return
        const sw = step as SwitchStep
        const next = newKey.trim()
        if (!next || next === oldKey) return
        if (!sw.cases || !hasOwn(sw.cases, oldKey) || hasOwn(sw.cases, next)) return
        // Rebuild preserving declaration order — the step card renders cases
        // in Object.entries order, so a rename must not reshuffle them.
        const rebuilt: Record<string, FlowStep[]> = {}
        for (const [k, v] of Object.entries(sw.cases)) {
          rebuilt[k === oldKey ? next : k] = v
        }
        sw.cases = rebuilt
        state.flowStepsDirty = true
        // A selection descending THROUGH the renamed case addresses it by
        // branch name — follow the rename so the selection stays alive (case
        // bodies are editable at any depth since the StepPath generalization).
        const ui = state as unknown as StepSelectionState
        const sel = ui.selectedStepPath
        if (
          sel != null &&
          sel.length > path.length &&
          sel[path.length] === `case:${oldKey}` &&
          path.every((seg, i) => sel[i] === seg)
        ) {
          const remapped = [...sel] as unknown as NestedStepPath
          remapped[path.length] = `case:${next}`
          ui.selectedStepPath = remapped
        }
      }),

    removeSwitchCase: (path, caseKey) =>
      set((state) => {
        const step = resolveStepAtPath(state.flowSteps, path)
        if (!step || step.type !== 'switch') return
        const sw = step as SwitchStep
        if (!sw.cases || !hasOwn(sw.cases, caseKey)) return
        delete sw.cases[caseKey]
        state.flowStepsDirty = true
      }),

    reorderSteps: (from, to) =>
      set((state) => {
        const steps = state.flowSteps
        if (from < 0 || from >= steps.length || to < 0 || to >= steps.length) return
        const [moved] = steps.splice(from, 1)
        steps.splice(to, 0, moved)
        state.flowStepsDirty = true
        remapTopLevelSelection(state, (i) => {
          if (i === from) return to
          if (from < i && to >= i) return i - 1
          if (from > i && to <= i) return i + 1
          return i
        })
      }),

    insertNestedStep: (path, step) =>
      set((state) => {
        const childIdx = path[path.length - 1] as number
        const branch = path[path.length - 2] as StepBranch
        const parent = resolveStepAtPath(state.flowSteps, path.slice(0, -2) as StepPath)
        // Saga.stages is a schema-level StageStep[] invariant, not a generic
        // nested branch. Enforce it at the store boundary as well as in the UI.
        if (branch === 'stages' && (parent?.type !== 'saga' || step.type !== 'stage')) return
        // create: a missing optional branch (else / default / new case) must
        // accept its first step instead of silently dropping the insert —
        // but ONLY the final branch; intermediate hops resolve strictly so a
        // stale path can never fabricate structure (the pre-#970 wrong-step
        // bug was an insert landing on the wrong step).
        const nested = resolveNestedStepArray(state.flowSteps, path, { create: true })
        if (!nested || childIdx < 0 || childIdx > nested.length) return
        nested.splice(childIdx, 0, step)
        state.flowStepsDirty = true
        remapNestedSelection(state, path, 'insert')
      }),

    removeNestedStep: (path) =>
      set((state) => {
        const childIdx = path[path.length - 1] as number
        const branch = path[path.length - 2] as StepBranch
        const parent = resolveStepAtPath(state.flowSteps, path.slice(0, -2) as StepPath)
        const nested = resolveNestedStepArray(state.flowSteps, path)
        if (nested && childIdx >= 0 && childIdx < nested.length) {
          // A saga must always retain at least one stage.
          if (branch === 'stages' && parent?.type === 'saga' && nested.length === 1) return
          nested.splice(childIdx, 1)
          // failurePolicy=continue is valid only while a non-empty onError
          // handler exists. Removing the final handler restores fail-stop.
          if (
            branch === 'onError' &&
            parent?.type === 'stage' &&
            nested.length === 0 &&
            (parent as StageStep).failurePolicy === 'continue'
          ) {
            (parent as StageStep).failurePolicy = 'stop'
          }
          pruneEmptyOptionalBranch(state.flowSteps, path)
          state.flowStepsDirty = true
          remapNestedSelection(state, path, 'remove')
        }
      }),

    // ---- Import/Export ----

    setCanvasState: (nodes, connections) =>
      set((state) => {
        state.nodes = nodes
        state.connections = connections
        state.selectedNodeIds = []
      }),
  }
}
